import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type RegisteredGame } from '../api-client.js';
import { useAuthStore } from '../auth/auth-store.js';
import { WelcomeCard } from '../components/WelcomeCard.js';
import TriBar from '../components/TriBar.js';

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; games: RegisteredGame[] }
  | { kind: 'error'; message: string };

export default function GamesPage() {
  const [state, setState] = useState<LoadState>({ kind: 'idle' });
  const [creating, setCreating] = useState<string | null>(null);
  const [creatingVsBot, setCreatingVsBot] = useState<string | null>(null);
  const [quickMatching, setQuickMatching] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    api
      .listGames()
      .then((games) => {
        if (!cancelled) setState({ kind: 'ready', games });
      })
      .catch((error: Error) => {
        if (!cancelled) {
          const message = error instanceof ApiError ? error.message : error.message;
          setState({ kind: 'error', message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCreateMatch(game: RegisteredGame) {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/' } } });
      return;
    }
    setCreateError(null);
    setCreating(game.key);
    try {
      const created = (await api.createMatch(game.key, user.id)) as {
        match: { id: string };
      };
      navigate(`/matches/${created.match.id}`);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to create match';
      setCreateError(message);
    } finally {
      setCreating(null);
    }
  }

  async function onPlayVsBot(game: RegisteredGame) {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/' } } });
      return;
    }
    setCreateError(null);
    setCreatingVsBot(game.key);
    try {
      const created = await api.createVsBotMatch(game.key, user.id);
      navigate(`/matches/${created.match.id}`);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to start vs-AI match';
      setCreateError(message);
    } finally {
      setCreatingVsBot(null);
    }
  }

  async function onQuickMatch(game: RegisteredGame) {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/' } } });
      return;
    }
    setCreateError(null);
    setQuickMatching(game.key);
    try {
      const result = await api.quickMatch(game.key, user.id);
      navigate(`/matches/${result.record.match.id}`);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to find a match';
      setCreateError(message);
    } finally {
      setQuickMatching(null);
    }
  }

  // Single source of truth for "any of the three flows is in flight" —
  // we disable every button while any one is running so a misclick
  // doesn't fire two creates.
  const anyBusy = (game: RegisteredGame) =>
    creating === game.key ||
    creatingVsBot === game.key ||
    quickMatching === game.key;

  // Box-art header per game — heritage guernsey tones (oxblood AFL, steel
  // cricket, amethyst TCG, pewter default), foil-underlined via ec-surface__head.
  const art = (key: string): { bg: string; glyph: string } => {
    if (key.includes('afl')) return { bg: 'bg-gradient-to-r from-ec-oxblood-deep to-ec-oxblood', glyph: '🏉' };
    if (key.includes('cricket')) return { bg: 'bg-gradient-to-r from-[#2c3e52] to-ec-ruck', glyph: '🏏' };
    if (key.includes('tcg')) return { bg: 'bg-gradient-to-r from-[#3a1f4d] to-[#6e3f97]', glyph: '🃏' };
    return { bg: 'bg-gradient-to-r from-ec-panel-2 to-ec-panel', glyph: '🎲' };
  };

  return (
    <section>
      {user && <WelcomeCard userId={user.id} />}
      <h2 className="mb-1 font-display text-lg font-semibold uppercase tracking-wide text-ec-face">Registered games</h2>
      <TriBar className="mb-4" />
      {state.kind === 'loading' && <p className="text-ec-bone">Loading…</p>}
      {state.kind === 'error' && (
        <p className="text-ec-oxblood-2">Failed to load games: {state.message}</p>
      )}
      {createError && <p className="mb-3 text-sm text-ec-oxblood-2">{createError}</p>}
      {state.kind === 'ready' && state.games.length === 0 && (
        <p className="text-ec-bone">No games registered yet.</p>
      )}
      {state.kind === 'ready' && state.games.length > 0 && (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {state.games.map((game, i) => (
            <li
              key={game.key}
              style={{ animationDelay: `${i * 70}ms` }}
              className="ec-surface flex animate-deal-in flex-col overflow-hidden transition-transform duration-200 hover:-translate-y-1 motion-reduce:animate-none"
            >
              {/* Box-art header strip — heritage guernsey + foil underline. */}
              <div className={`ec-surface__head !px-4 !py-3 ${art(game.key).bg}`}>
                <h3 className="ec-surface__title !text-base">
                  {game.name}
                </h3>
                <span className="select-none text-3xl drop-shadow-[0_2px_3px_rgba(0,0,0,0.45)]">
                  {art(game.key).glyph}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4 pt-3">
              <p className="font-mono text-xs text-ec-bone-dim">
                {game.key} <span className="text-ec-bone-dim">· v{game.version}</span>
              </p>
              <dl className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt className="text-ec-bone-dim">Seats</dt>
                  <dd className="text-ec-bone">{game.seatKeys.join(', ')}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ec-bone-dim">Turn timer</dt>
                  <dd className="text-ec-bone">{game.defaultTurnSeconds}s</dd>
                </div>
              </dl>
              {/*
                Three flows, ordered by what a new user should reach for first:
                  1. Quick match   — primary, oxblood. "Find me an opponent."
                  2. Play vs AI    — secondary, pine. Instant fallback.
                  3. Create match  — tertiary, steel outline. Share-URL flow
                     for playing a specific friend.
              */}
              <button
                type="button"
                onClick={() => onQuickMatch(game)}
                disabled={anyBusy(game) || !user}
                className="ec-btn ec-btn--primary mt-4 w-full"
              >
                {quickMatching === game.key
                  ? 'Finding a match…'
                  : user
                    ? '⚡ Quick match'
                    : 'Log in to play'}
              </button>
              {user && (
                <>
                  <button
                    type="button"
                    onClick={() => onPlayVsBot(game)}
                    disabled={anyBusy(game)}
                    className="ec-btn mt-2 w-full"
                  >
                    {creatingVsBot === game.key ? 'Starting…' : '🤖 Play vs AI'}
                  </button>
                  <button
                    type="button"
                    onClick={() => onCreateMatch(game)}
                    disabled={anyBusy(game)}
                    className="ec-btn mt-2 w-full"
                  >
                    {creating === game.key ? 'Creating…' : 'Create private match'}
                  </button>
                </>
              )}
              {game.key === 'afl-microformat' && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Link
                    to={`/library/${game.key}`}
                    className="ec-btn w-full"
                  >
                    🃏 Cards
                  </Link>
                  <Link
                    to={`/decks/${game.key}`}
                    className="ec-btn w-full"
                  >
                    📚 Decks
                  </Link>
                </div>
              )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
